import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ExerciseService from "services/ExerciseService";
import exerciseConstants from "constants/Exercises/exerciseConstants";

/**
 * Hook customizado responsável por gerenciar o ciclo de vida de uma tarefa (exercício) ativa.
 *
 * Este hook centraliza toda a lógica relacionada a:
 * - Recuperação do exercício via parâmetro `exerciseId` na URL
 * - Mapeamento e adaptação dos dados para o frontend
 * - Envio de respostas para validação no backend
 * - Controle de feedback visual (alertas e loading)
 * - Gerenciamento do estado do HUD (abertura/fechamento)
 * - Redirecionamento após finalização da tarefa
 *
 * Suporta múltiplos tipos de exercícios, como:
 * - MACHINE (autômatos)
 * - GRAMMAR (gramáticas)
 *
 * @function useExerciseTask
 *
 * @param {Object} params - Parâmetros de configuração do hook
 *
 * @param {Object} params.credential - Credenciais do usuário autenticado
 * @param {boolean} params.isAdmin - Indica se o usuário possui privilégios administrativos
 * @param {string} params.language - Idioma atual da interface (ex: "pt", "en")
 *
 * @param {Function} params.setAlert - Função para exibir alertas globais
 * @param {Function} params.setLoading - Função para controle de loading global
 *
 * @param {string} params.defaultType - Tipo base do exercício ("MACHINE" ou "GRAMMAR")
 *
 * @returns {Object} Retorna um objeto contendo:
 * @returns {Object|null} return.currentTask - Tarefa atual carregada (ou null se inexistente)
 * @returns {Function} return.handleSend - Função para envio da resposta ao backend
 * @returns {Function} return.handleCloseTaskHud - Função para fechar o HUD e redirecionar
 *
 * @example
 * const {
 *   currentTask,
 *   handleSend,
 *   handleCloseTaskHud
 * } = useExerciseTask({
 *   credential,
 *   isAdmin: false,
 *   language: "pt",
 *   setAlert,
 *   setLoading,
 *   defaultType: "GRAMMAR"
 * });
 *
 * @author Willian
 */

export function useExerciseTask({
  credential,
  isAdmin,
  language,
  setAlert,
  setLoading,
  defaultType,
}) {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const exerciseId = query.get("exerciseId");

  const [currentTask, setCurrentTask] = useState(null);
  const hudLabels = exerciseConstants.hud;

  /**
   * Efeito responsável por carregar os dados do exercício ativo.
   *
   * Fluxo:
   * - Obtém o `exerciseId` da URL
   * - Valida presença de credenciais
   * - Busca lista de exercícios no backend
   * - Filtra o exercício correspondente
   * - Adapta os campos multi-idioma (title/description)
   * - Atualiza o estado `currentTask`
   *
   * Dependências:
   * - exerciseId, credential, isAdmin, language, defaultType
   */

  useEffect(() => {
    async function loadTask() {
      if (!exerciseId) {
        setCurrentTask(null);
        return;
      }
      if (!credential) {
        setCurrentTask(null);
        return;
      }
      try {
        const list = await ExerciseService.list(
          defaultType,
          credential,
          isAdmin
        );
        const found = list.find((ex) => {
          return ex.id === exerciseId;
        });
        if (found) {
          let titleObj = {};
          if (found.title) {
            titleObj = found.title;
          }
          let descObj = {};
          if (found.description) {
            descObj = found.description;
          }
          setCurrentTask({
            id: found.id,
            description: descObj[language],
            title: titleObj[language],
            type: found.type,
          });
        }
      } catch (err) {
        console.error("Erro ao carregar exercício:", err);
      }
    }
    loadTask();
  }, [exerciseId, credential, isAdmin, language, defaultType]);

  /**
   * Envia a resposta do usuário para validação no backend.
   *
   * Responsabilidades:
   * - Ativar estado de loading global
   * - Realizar chamada ao serviço de validação
   * - Interpretar o resultado retornado
   * - Disparar alertas de sucesso, erro ou falha de conexão
   *
   * @async
   * @function handleSend
   *
   * @param {any} content - Conteúdo da solução (ex: DOT, gramática, regex, etc.)
   * @param {string} type - Tipo da validação a ser executada
   *
   * @returns {Promise<Object|null>} Resultado da validação retornado pelo backend
   */
  const handleSend = async (content, type) => {
    setLoading(true);
    try {
      const result = await ExerciseService.validate(
        exerciseId,
        content,
        credential,
        type
      );

      let alertColor = "success";
      let alertText = "";
      if (result) {
        if (
          result.errorMessage &&
          Object.keys(result.errorMessage).length > 0
        ) {
          const langKey = language.toUpperCase();
          alertText = result.errorMessage[langKey] || hudLabels.error[language];
          alertColor = "warning";
        } else {
          alertText = hudLabels.success[language];
          alertColor = "success";
        }
      } else {
        alertText = hudLabels.error[language];
        alertColor = "warning";
      }

      setAlert({
        active: true,
        text: alertText,
        color: alertColor,
      });
      return result;
    } catch (e) {
      setAlert({
        active: true,
        text: hudLabels.connError[language],
        color: "danger",
      });
      return { errorMessage: hudLabels.connError[language] };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Finaliza a interação com a tarefa ativa.
   *
   * Ações executadas:
   * - Limpa o estado da tarefa atual
   * - Redireciona o usuário para a tela de seleção de exercícios
   * - Reabre automaticamente o seletor de exercícios
   *
   * O destino varia conforme o tipo base:
   * - MACHINE → /user/automaton
   * - GRAMMAR → /user/grammar
   *
   * @function handleCloseTaskHud
   */
  const handleCloseTaskHud = () => {
    let targetRoute = "grammar";
    if (defaultType === "MACHINE") {
      targetRoute = "automaton";
    }

    setCurrentTask(null);
    history.push({
      pathname: "/user/" + targetRoute,
      search: "",
      state: {
        openSelector: true,
        type: defaultType,
      },
    });
  };

  return {
    currentTask,
    handleSend,
    handleCloseTaskHud,
  };
}
